/**
 * AuditCheckout.js — /audit/checkout
 * v2 — Navy/Teal/Green color scheme matching AuditLanding v2
 */
import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

const API      = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STRIPE_PK = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;

const NAVY  = "#0F2D52";
const TEAL  = "#0B6E6E";
const GREEN = "#1A6B3A";

// Deferred Stripe.js — only loads when user clicks "Continue to Payment"
let stripeLoadPromise = null;
const getStripe = () => {
  if (!stripeLoadPromise) {
    stripeLoadPromise = import('@stripe/stripe-js').then(({ loadStripe }) =>
      loadStripe(STRIPE_PK)
    );
  }
  return stripeLoadPromise;
};

export default function AuditCheckout() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id") || localStorage.getItem("quiz_session_id");

  const [pricing, setPricing]         = useState({ price_display: "$17", price_cents: 1700, is_launch: true, spots_remaining: 200 });
  const [firstName, setFirstName]     = useState("");
  const [email, setEmail]             = useState("");
  const [bump, setBump]               = useState(false);
  const [clientSecret, setClientSecret] = useState(null);
  const [paymentIntentId, setPaymentIntentId] = useState(null);
  const [loading, setLoading]         = useState(false);
  const [submitting, setSubmitting]   = useState(false);
  const [stripe, setStripe]           = useState(null);
  const [elements, setElements]       = useState(null);
  const paymentElementRef = useRef(null);
  const elementsRef       = useRef(null);

  useEffect(() => {
    axios.get(`${API}/quiz/pricing`).then(r => setPricing(r.data)).catch(() => {});
  }, []);

  const createIntent = async () => {
    if (!firstName.trim() || !email.trim()) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/quiz/create-payment-intent`, {
        session_id: sessionId || "guest",
        first_name: firstName.trim(),
        email:      email.trim(),
        add_bump:   bump,
      });
      setClientSecret(res.data.client_secret);
      setPaymentIntentId(res.data.payment_intent_id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to initialize payment. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!clientSecret || !paymentElementRef.current) return;
    let mounted = true;
    (async () => {
      const stripeInstance = await getStripe();
      if (!mounted || !stripeInstance) return;
      setStripe(stripeInstance);
      if (elementsRef.current) {
        try { elementsRef.current.getElement("payment")?.unmount(); } catch {}
      }
      const newElements = stripeInstance.elements({
        clientSecret,
        appearance: {
          theme: "stripe",
          variables: { colorPrimary: GREEN, borderRadius: "6px", fontFamily: "system-ui, sans-serif" },
        },
      });
      elementsRef.current = newElements;
      setElements(newElements);
      const paymentElement = newElements.create("payment", {
        layout: "tabs",
        fields: { billingDetails: { email: "never", name: "never" } },
      });
      paymentElement.mount(paymentElementRef.current);
    })();
    return () => { mounted = false; };
  }, [clientSecret]);

  // Recreate intent when bump amount changes
  useEffect(() => {
    if (clientSecret) {
      setClientSecret(null);
      setElements(null);
      if (paymentElementRef.current) paymentElementRef.current.innerHTML = "";
      createIntent();
    }
  }, [bump]); // intentionally excludes createIntent to avoid infinite loop

  const handlePay = async () => {
    if (!stripe || !elements) { toast.error("Payment form not ready. Please wait."); return; }
    setSubmitting(true);
    try {
      const { error } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/audit/founding?session_id=${sessionId || ""}&payment_intent_id=${paymentIntentId || ""}`,
          payment_method_data: { billing_details: { name: firstName, email } },
        },
      });
      if (error) toast.error(error.message || "Payment failed. Please try again.");
    } catch {
      toast.error("An unexpected error occurred. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const totalCents   = pricing.price_cents + (bump ? 1000 : 0);
  // ── Commandment II: use backend-computed display strings ──
  const totalDisplay = bump
    ? (pricing.total_with_bump_display    ?? `$${totalCents / 100}`)
    : (pricing.total_without_bump_display ?? `$${totalCents / 100}`);
  const canCreate    = firstName.trim().length > 0 && email.trim().length > 0;

  return (
    <div style={{ fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                  minHeight: "100vh", background: "#f5f5f4", overflowX: "hidden" }}>

      <style>{`
        @media (max-width: 700px) {
          .checkout-grid { grid-template-columns: 1fr !important; }
        }
        input:focus { border-color: ${TEAL} !important; outline: none; }
      `}</style>

      {/* Nav */}
      <nav style={{ background: NAVY, padding: "14px 24px",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    borderBottom: `1px solid #1a3d6b` }}>
        <span style={{ fontWeight: 800, fontSize: 18, color: "#6ee7b7" }}>SolelyTax AI</span>
      </nav>

      {/* Progress / urgency bar */}
      {pricing.is_launch && (
        <div style={{ background: TEAL, padding: "10px 24px", textAlign: "center",
                      fontSize: 13, fontWeight: 700, color: "#fff" }}>
          ⚡ Launch pricing: <span data-testid="checkout-spots">{pricing.spots_remaining}</span> of 200 spots remaining at $17.
          After 200: $27. No exceptions.
        </div>
      )}

      <div style={{ maxWidth: 920, margin: "36px auto", padding: "0 20px" }}>

        {/* Headline */}
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <h1 style={{ fontSize: "clamp(20px,3.5vw,30px)", fontWeight: 800,
                        color: NAVY, marginBottom: 8, letterSpacing: "-0.015em" }}>
            Complete Your Order — Your Report Is Being Prepared
          </h1>
          <p style={{ color: "#57534e", fontSize: 15, lineHeight: 1.6 }}>
            Your personalized 15–20 page CPA Readiness Report will be in your inbox within minutes of payment.
          </p>
        </div>

        {/* Two-column grid */}
        <div className="checkout-grid"
          style={{ display: "grid", gridTemplateColumns: "1fr 1.45fr", gap: 28, alignItems: "start" }}>

          {/* LEFT: Order Summary */}
          <div style={{ background: "#fff", border: `1px solid #e7e5e4`,
                        borderTop: `4px solid ${NAVY}`, borderRadius: 4, padding: 24 }}>

            <div style={{ fontWeight: 800, fontSize: 15, color: NAVY, marginBottom: 16 }}>
              Order Summary
            </div>

            {/* Price */}
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontWeight: 700, fontSize: 14, color: "#1c1917", marginBottom: 4 }}>
                CPA Readiness Audit Report
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {pricing.is_launch && (
                  <span style={{ color: "#9ca3af", textDecoration: "line-through", fontSize: 14 }}>$27</span>
                )}
                <span style={{ color: GREEN, fontWeight: 900, fontSize: 22 }}>
                  {pricing.price_display} today
                </span>
              </div>
              {pricing.is_launch && (
                <div style={{ fontSize: 11, color: TEAL, fontWeight: 700, marginTop: 3 }}>
                  Launch pricing — {pricing.spots_remaining} spots remaining
                </div>
              )}
            </div>

            {/* Included */}
            <div style={{ borderTop: "1px solid #f5f5f4", paddingTop: 14 }}>
              {[
                "15–20 page personalized PDF report",
                "CPA Readiness Score (0–100)",
                "Potential tax opportunity range (estimated)",
                "Strategy cards for every applicable gap",
                "CPA meeting agenda — ready to hand over",
                "90-day implementation roadmap",
                "7-day quick wins plan",
                "Instant delivery to your inbox",
              ].map((f, i) => (
                <div key={i} style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8 }}>
                  <span style={{ color: GREEN, fontSize: 13 }}>✅</span>
                  <span style={{ fontSize: 13, color: "#374151" }}>{f}</span>
                </div>
              ))}
            </div>

            {/* Bump line item */}
            {bump && (
              <div style={{ borderTop: "1px solid #f5f5f4", paddingTop: 12, marginTop: 4 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: TEAL }}>
                  ➕ Video Walkthrough — $10
                </div>
                <div style={{ fontSize: 12, color: "#78716c", marginTop: 3 }}>
                  15-min walkthrough showing how to use your report with your CPA
                </div>
              </div>
            )}

            {/* Total */}
            <div style={{ borderTop: "1px solid #e7e5e4", marginTop: 16, paddingTop: 16,
                          display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontWeight: 700, color: "#1c1917", fontSize: 14 }}>Total</span>
              <span style={{ fontWeight: 900, fontSize: 24, color: GREEN }}>{totalDisplay}</span>
            </div>

            {/* Guarantee */}
            <div style={{ marginTop: 16, padding: "12px 14px",
                          background: "#f0fdf4", border: `1px solid #a7f3d0`,
                          borderLeft: `3px solid ${GREEN}`,
                          fontSize: 12, color: "#065f46", lineHeight: 1.6 }}>
              🛡️ <strong>30-day money-back guarantee.</strong> No questions asked. You can keep the report.
            </div>
          </div>

          {/* RIGHT: Payment Form */}
          <div style={{ background: "#fff", border: "1px solid #e7e5e4",
                        borderTop: `4px solid ${TEAL}`, borderRadius: 4, padding: 24 }}>

            {/* Name + Email */}
            <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 20 }}>
              <input
                value={firstName}
                onChange={e => setFirstName(e.target.value)}
                placeholder="First name"
                data-testid="checkout-first-name"
                style={{ border: "2px solid #e7e5e4", borderRadius: 6,
                         padding: "12px 14px", fontSize: 15,
                         boxSizing: "border-box", width: "100%", transition: "border-color 0.15s" }}
              />
              <input
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="Email address — your report is delivered here"
                type="email"
                data-testid="checkout-email"
                style={{ border: "2px solid #e7e5e4", borderRadius: 6,
                         padding: "12px 14px", fontSize: 15,
                         boxSizing: "border-box", width: "100%", transition: "border-color 0.15s" }}
              />
            </div>

            {/* Continue to payment */}
            {!clientSecret && (
              <button
                onClick={createIntent}
                disabled={!canCreate || loading}
                data-testid="checkout-load-payment"
                style={{
                  background: canCreate ? NAVY : "#e7e5e4",
                  color: canCreate ? "#fff" : "#9ca3af",
                  border: "none", borderRadius: 6, padding: "13px",
                  fontSize: 15, fontWeight: 700, cursor: canCreate ? "pointer" : "default",
                  width: "100%", marginBottom: 16, transition: "all 0.15s",
                }}>
                {loading ? "Loading payment form..." : "Continue to Payment →"}
              </button>
            )}

            {/* Stripe Elements + bump */}
            {clientSecret && (
              <>
                <div ref={paymentElementRef} style={{ marginBottom: 16 }} />

                {/* Order bump */}
                <div
                  onClick={() => setBump(!bump)}
                  style={{ background: bump ? "#f0fdf4" : "#f8fafc",
                           border: bump ? `2px solid ${GREEN}` : "2px solid #e7e5e4",
                           borderRadius: 6, padding: "14px 16px",
                           marginBottom: 16, cursor: "pointer", transition: "all 0.15s" }}>
                  <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                    <input
                      type="checkbox"
                      checked={bump}
                      onChange={e => setBump(e.target.checked)}
                      data-testid="order-bump-checkbox"
                      style={{ marginTop: 3, width: 16, height: 16, cursor: "pointer",
                               accentColor: GREEN }}
                    />
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 14, color: NAVY }}>
                        ➕ Add Video Walkthrough — +$10
                      </div>
                      <div style={{ fontSize: 13, color: "#374151", marginTop: 4, lineHeight: 1.55 }}>
                        A 15-minute video walkthrough of your report — which strategies to prioritize,
                        what to say to your CPA, and common mistakes to avoid.
                      </div>
                      <div style={{ fontSize: 12, color: TEAL, fontWeight: 600, marginTop: 5 }}>
                        Most buyers add this — it makes the report 3x more actionable.
                      </div>
                    </div>
                  </div>
                </div>

                {/* Pay */}
                <button
                  onClick={handlePay}
                  disabled={submitting}
                  data-testid="checkout-pay-btn"
                  style={{ background: submitting ? "#6b9e7a" : GREEN, color: "#fff",
                           border: "none", borderRadius: 6, padding: "16px",
                           fontSize: 17, fontWeight: 700, cursor: "pointer",
                           width: "100%", transition: "background 0.15s" }}>
                  {submitting ? "Processing..." : `Get My Report for ${totalDisplay} →`}
                </button>
              </>
            )}

            {/* Trust */}
            <div style={{ marginTop: 16, textAlign: "center" }}>
              <div style={{ fontSize: 12, color: "#78716c", marginBottom: 8 }}>
                🔒 Secure 256-bit encrypted checkout powered by Stripe
              </div>
              <div style={{ display: "flex", justifyContent: "center", gap: 8, flexWrap: "wrap" }}>
                {["Visa", "Mastercard", "Amex", "Discover"].map(c => (
                  <span key={c} style={{ background: "#f5f5f4", border: "1px solid #e7e5e4",
                                         padding: "3px 8px", borderRadius: 3,
                                         fontSize: 11, color: "#57534e" }}>{c}</span>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Social proof */}
        <div style={{ textAlign: "center", marginTop: 28,
                      color: "#78716c", fontSize: 13, lineHeight: 1.7 }}>
          <span style={{ color: "#fbbf24" }}>★★★★★</span>
          {" "}Joined by 300+ high-earning 1099 professionals
          {" · "}30-day money-back guarantee
        </div>
      </div>
    </div>
  );
}
